import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import DOMPurify from "dompurify";
import { 
  FiUser, 
  FiSettings, 
  FiLogOut, 
  FiCheck, 
  FiEdit2,
  FiSave,
  FiLoader,
  FiMail,
  FiPhone,
  FiShield,
  FiKey,
  FiCalendar,
  FiAward,
  FiUsers,
  FiFileText,
  FiBell,
  FiGlobe,
  FiMenu,
  FiX
} from "react-icons/fi";
import { 
  HiOutlineUserCircle,
  HiOutlineCog,
  HiOutlineShieldCheck,
  HiOutlineIdentification
} from "react-icons/hi";

// Professional color scheme - consistent and accessible
const colors = {
  primary: '#2563eb',
  secondary: '#475569',
  accent: '#3b82f6',
  success: '#10b981',
  warning: '#f59e0b',
  error: '#ef4444',
  lightBg: '#f8fafc',
  cardBg: '#ffffff',
  border: '#e2e8f0',
  textDark: '#1e293b',
  textMedium: '#475569',
  textLight: '#64748b',
  gradient: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)'
};

const AdminProfile = () => {
  const [isEditing, setIsEditing] = useState(false);
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showLogoutSuccess, setShowLogoutSuccess] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const navigate = useNavigate();
  const { logout } = useAuth();

  // Handle responsive layout
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Input sanitization function
  const sanitizeInput = (input) => {
    if (typeof input !== 'string') return input;
    return DOMPurify.sanitize(input.trim());
  };

  // Validate email format
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const token = localStorage.getItem("authToken");
        const storedUserData = localStorage.getItem("userData");

        if (!token || !storedUserData) {
          navigate("/login");
          return;
        }

        // Simulate API call delay for better UX
        await new Promise(resolve => setTimeout(resolve, 800));

        const parsedData = JSON.parse(storedUserData);
        
        // Validate and sanitize user data
        const sanitizedData = {
          ...parsedData,
          full_name: sanitizeInput(parsedData.full_name || ''),
          email: validateEmail(parsedData.email) ? sanitizeInput(parsedData.email) : '',
          membership_id: sanitizeInput(parsedData.membership_id || ''),
          role: sanitizeInput(parsedData.role || 'admin'),
          permissions: parsedData.permissions || ['user-management', 'content-moderation', 'system-config', 'analytics']
        };

        setUserData(sanitizedData);
      } catch (error) {
        console.error("Error loading user data:", error);
        setError("Failed to load profile data. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    fetchUserData();
  }, [navigate]);

  const handleEditToggle = async () => {
    if (isEditing) {
      // Save changes
      setSaveLoading(true);
      try {
        // Simulate API call
        await new Promise(resolve => setTimeout(resolve, 1000));
        // In real app, you would send updated data to backend
        setIsEditing(false);
      } catch (error) {
        setError("Failed to save changes. Please try again.");
      } finally {
        setSaveLoading(false);
      }
    } else {
      setIsEditing(true);
    }
  };

  const handleLogout = () => {
    setShowLogoutSuccess(true);
    setTimeout(() => {
      logout();
      navigate("/login");
    }, 2000);
  };

  // Responsive styles based on screen size
  const styles = {
    profileContainer: {
      maxWidth: '1200px',
      margin: '0 auto',
      padding: isMobile ? '16px 12px' : '30px 20px',
      background: colors.lightBg,
      minHeight: '100vh',
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
      overflowX: 'hidden'
    },
    skeletonHeader: {
      height: isMobile ? '80px' : '120px',
      background: colors.cardBg,
      borderRadius: '16px',
      marginBottom: '16px',
      animation: 'pulse 2s infinite'
    },
    skeletonCard: {
      height: isMobile ? '150px' : '200px',
      background: colors.cardBg,
      borderRadius: '16px',
      marginBottom: '16px',
      animation: 'pulse 2s infinite'
    },
    errorMessage: {
      background: '#fef2f2',
      color: colors.error,
      padding: isMobile ? '16px' : '20px',
      borderRadius: '12px',
      textAlign: 'center',
      margin: isMobile ? '20px 0' : '40px 20px',
      border: `1px solid ${colors.error}20`,
      fontSize: isMobile ? '14px' : '16px',
      fontWeight: '500'
    },
    logoutModalOverlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(15, 23, 42, 0.8)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 1000,
      backdropFilter: 'blur(8px)',
      padding: isMobile ? '16px' : '0'
    },
    logoutModal: {
      background: colors.cardBg,
      padding: isMobile ? '24px' : '40px',
      borderRadius: '20px',
      textAlign: 'center',
      maxWidth: '440px',
      width: '100%',
      boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
      border: `1px solid ${colors.border}`,
      margin: isMobile ? '16px' : '0'
    },
    profileHeader: {
      textAlign: 'center',
      marginBottom: isMobile ? '32px' : '48px',
      padding: isMobile ? '40px 0 20px' : '60px 0 40px'
    },
    profileTitle: {
      color: colors.textDark,
      margin: '0 0 12px 0',
      fontSize: isMobile ? '1.75rem' : '2.5rem',
      fontWeight: '700',
      background: colors.gradient,
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
      backgroundClip: 'text',
      lineHeight: 1.2
    },
    profileSubtitle: {
      color: colors.textMedium,
      fontSize: isMobile ? '1rem' : '1.125rem',
      fontWeight: '500',
      margin: 0,
      lineHeight: 1.4
    },
    card: {
      background: colors.cardBg,
      borderRadius: isMobile ? '12px' : '20px',
      padding: isMobile ? '20px' : '32px',
      marginBottom: isMobile ? '16px' : '24px',
      boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
      border: `1px solid ${colors.border}`,
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
    },
    membershipStatus: {
      background: colors.gradient,
      color: colors.cardBg,
      display: 'grid',
      gridTemplateColumns: isMobile ? '1fr' : 'auto 1fr auto',
      gap: isMobile ? '16px' : '24px',
      alignItems: 'center',
      position: 'relative',
      overflow: 'hidden',
      textAlign: isMobile ? 'center' : 'left'
    },
    statusPattern: {
      position: 'absolute',
      top: 0,
      right: 0,
      bottom: 0,
      left: 0,
      background: 'radial-gradient(circle at 20% 80%, rgba(255,255,255,0.1) 0%, transparent 50%)',
      pointerEvents: 'none'
    },
    profileImgPlaceholder: {
      width: isMobile ? '60px' : '80px',
      height: isMobile ? '60px' : '80px',
      borderRadius: '50%',
      border: `3px solid rgba(255,255,255,0.3)`,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: isMobile ? '1.25rem' : '1.5rem',
      fontWeight: 'bold',
      background: 'rgba(255,255,255,0.1)',
      backdropFilter: 'blur(10px)',
      margin: isMobile ? '0 auto' : '0'
    },
    profileName: {
      margin: '0 0 8px 0',
      fontSize: isMobile ? '1.25rem' : '1.75rem',
      fontWeight: '700',
      display: 'flex',
      flexDirection: isMobile ? 'column' : 'row',
      alignItems: isMobile ? 'center' : 'flex-start',
      gap: isMobile ? '8px' : '12px'
    },
    adminBadge: {
      background: 'rgba(255, 255, 255, 0.2)',
      padding: '4px 12px',
      borderRadius: '16px',
      fontSize: isMobile ? '0.7rem' : '0.75rem',
      fontWeight: '600',
      backdropFilter: 'blur(10px)',
      border: '1px solid rgba(255,255,255,0.3)',
      display: 'inline-flex',
      alignItems: 'center',
      gap: '4px',
      width: isMobile ? 'fit-content' : 'auto'
    },
    sectionHeader: {
      marginBottom: isMobile ? '16px' : '24px',
      paddingBottom: isMobile ? '12px' : '16px',
      borderBottom: `1px solid ${colors.border}`
    },
    sectionTitle: {
      margin: '0',
      color: colors.textDark,
      fontSize: isMobile ? '1.125rem' : '1.25rem',
      fontWeight: '600',
      display: 'flex',
      alignItems: 'center',
      gap: isMobile ? '8px' : '12px'
    },
    sectionIcon: {
      color: colors.accent,
      fontSize: isMobile ? '18px' : '20px'
    },
    formGrid: {
      display: 'grid',
      gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(280px, 1fr))',
      gap: isMobile ? '16px' : '20px'
    },
    inputGroup: {
      position: 'relative'
    },
    inputIcon: {
      position: 'absolute',
      left: '12px',
      top: '50%',
      transform: 'translateY(-50%)',
      color: colors.textLight,
      fontSize: isMobile ? '16px' : '18px'
    },
    label: {
      display: 'block',
      marginBottom: '6px',
      fontWeight: '500',
      color: colors.textDark,
      fontSize: isMobile ? '0.8rem' : '0.875rem'
    },
    input: {
      width: '100%',
      padding: isMobile ? '10px 12px 10px 36px' : '12px 16px 12px 40px',
      border: `1px solid ${colors.border}`,
      borderRadius: '8px',
      fontSize: isMobile ? '0.8rem' : '0.875rem',
      transition: 'all 0.2s ease',
      backgroundColor: colors.lightBg,
      color: colors.textDark
    },
    adminInfoGrid: {
      display: 'grid',
      gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(300px, 1fr))',
      gap: isMobile ? '16px' : '24px'
    },
    permissionsList: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: '6px',
      marginTop: '12px',
      justifyContent: isMobile ? 'center' : 'flex-start'
    },
    permissionTag: {
      background: colors.gradient,
      color: colors.cardBg,
      padding: isMobile ? '4px 10px' : '6px 12px',
      borderRadius: '16px',
      fontSize: isMobile ? '0.7rem' : '0.75rem',
      fontWeight: '500',
      display: 'flex',
      alignItems: 'center',
      gap: '4px'
    },
    cardActions: {
      display: 'flex',
      gap: isMobile ? '12px' : '16px',
      marginTop: isMobile ? '24px' : '32px',
      justifyContent: 'center',
      flexDirection: isMobile ? 'column' : 'row'
    },
    editBtn: {
      background: colors.gradient,
      color: colors.cardBg,
      border: 'none',
      padding: isMobile ? '10px 20px' : '12px 32px',
      borderRadius: '8px',
      cursor: 'pointer',
      fontSize: isMobile ? '0.8rem' : '0.875rem',
      fontWeight: '600',
      transition: 'all 0.2s ease',
      minWidth: isMobile ? '100%' : '140px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '8px'
    },
    logoutContainer: {
      display: 'flex',
      justifyContent: 'center',
      marginTop: isMobile ? '32px' : '48px',
      padding: isMobile ? '24px 0' : '40px 0',
      borderTop: `1px solid ${colors.border}`
    },
    logoutBtn: {
      background: 'transparent',
      color: colors.error,
      border: `1px solid ${colors.error}`,
      padding: isMobile ? '10px 20px' : '12px 32px',
      borderRadius: '8px',
      cursor: 'pointer',
      fontSize: isMobile ? '0.8rem' : '0.875rem',
      fontWeight: '600',
      transition: 'all 0.2s ease',
      minWidth: isMobile ? '100%' : '140px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '8px'
    },
    membershipCardContent: {
      background: colors.gradient,
      color: colors.cardBg,
      padding: isMobile ? '20px' : '32px',
      borderRadius: '12px',
      display: 'grid',
      gridTemplateColumns: isMobile ? '1fr' : '1fr auto',
      gap: isMobile ? '20px' : '32px',
      alignItems: 'center',
      textAlign: isMobile ? 'center' : 'left'
    },
    membershipIdSection: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      justifyContent: isMobile ? 'center' : 'flex-start',
      marginBottom: '12px'
    },
    membershipBadges: {
      display: 'flex',
      gap: '6px',
      flexWrap: 'wrap',
      justifyContent: isMobile ? 'center' : 'flex-start'
    }
  };

  // Loading Skeleton Component
  const LoadingSkeleton = () => (
    <div style={styles.profileContainer}>
      <div style={styles.skeletonHeader}></div>
      <div style={styles.skeletonCard}></div>
      <div style={styles.skeletonCard}></div>
      <div style={styles.skeletonCard}></div>
    </div>
  );

  // Permission icons mapping
  const permissionIcons = {
    'user-management': <FiUsers size={12} />,
    'content-moderation': <FiFileText size={12} />,
    'system-config': <FiSettings size={12} />,
    'analytics': <FiGlobe size={12} />,
    'security': <FiShield size={12} />,
    'notifications': <FiBell size={12} />
  };

  const getPermissionIcon = (permission) => {
    const key = permission.toLowerCase();
    return permissionIcons[key] || <FiKey size={12} />;
  };

  if (loading) {
    return <LoadingSkeleton />;
  }

  if (error) {
    return (
      <div style={styles.profileContainer}>
        <div style={styles.errorMessage}>
          {error}
          <button 
            onClick={() => window.location.reload()} 
            style={{
              marginLeft: '12px',
              background: colors.primary,
              color: 'white',
              border: 'none',
              padding: '8px 16px',
              borderRadius: '6px',
              cursor: 'pointer',
              display: isMobile ? 'block' : 'inline-flex',
              alignItems: 'center',
              gap: '4px',
              marginTop: isMobile ? '8px' : '0',
              width: isMobile ? '100%' : 'auto'
            }}
          >
            <FiLoader size={14} />
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!userData) {
    return (
      <div style={styles.profileContainer}>
        <div style={styles.errorMessage}>
          No user data found. Please login again.
          <button 
            onClick={() => navigate('/login')}
            style={{
              marginLeft: isMobile ? '0' : '12px',
              background: colors.primary,
              color: 'white',
              border: 'none',
              padding: '8px 16px',
              borderRadius: '6px',
              cursor: 'pointer',
              display: isMobile ? 'block' : 'inline-flex',
              alignItems: 'center',
              gap: '4px',
              marginTop: isMobile ? '8px' : '0',
              width: isMobile ? '100%' : 'auto'
            }}
          >
            <FiLogOut size={14} />
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  const nameParts = userData.full_name?.split(' ') || ['', ''];
  const firstName = nameParts[0] || '';
  const lastName = nameParts.slice(1).join(' ') || '';

  return (
    <div style={styles.profileContainer}>
      <style>
        {`
          @keyframes pulse {
            0% { opacity: 1; }
            50% { opacity: 0.5; }
            100% { opacity: 1; }
          }

          @keyframes fadeIn {
            from { opacity: 0; transform: translateY(20px); }
            to { opacity: 1; transform: translateY(0); }
          }

          @keyframes slideUp {
            from {
              opacity: 0;
              transform: translateY(30px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }

          @keyframes spin {
            to { transform: rotate(360deg); }
          }

          @keyframes progressFill {
            0% { width: 0%; }
            100% { width: 100%; }
          }

          .card-hover:hover {
            transform: translateY(-4px);
            box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
          }

          .btn-hover:hover {
            transform: translateY(-2px);
            box-shadow: 0 8px 25px rgba(37, 99, 235, 0.3);
          }

          .logout-btn-hover:hover {
            transform: translateY(-2px);
            box-shadow: 0 8px 25px rgba(239, 68, 68, 0.3);
            background: #ef4444;
            color: white;
          }

          input:focus {
            outline: none;
            border-color: ${colors.accent};
            box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
          }

          .fade-in {
            animation: fadeIn 0.6s ease-out;
          }

          /* Mobile-specific optimizations */
          @media (max-width: 480px) {
            .mobile-text-sm {
              font-size: 0.875rem;
            }
            
            .mobile-p-0 {
              padding: 0;
            }
          }

          /* Prevent horizontal scrolling */
          body {
            overflow-x: hidden;
          }
        `}
      </style>

      {/* Logout Success Modal */}
      {showLogoutSuccess && (
        <div style={styles.logoutModalOverlay} className="fade-in">
          <div style={styles.logoutModal}>
            <div style={{
              width: isMobile ? '50px' : '60px',
              height: isMobile ? '50px' : '60px',
              background: colors.gradient,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 16px',
              fontSize: isMobile ? '20px' : '24px',
              color: colors.cardBg
            }}>
              <FiCheck size={isMobile ? 20 : 24} />
            </div>
            <h3 style={{ 
              color: colors.textDark, 
              margin: '0 0 12px 0', 
              fontSize: isMobile ? '18px' : '20px', 
              fontWeight: '600' 
            }}>
              Logged Out Successfully
            </h3>
            <p style={{ 
              color: colors.textMedium, 
              margin: '0 0 20px 0', 
              fontSize: isMobile ? '13px' : '14px' 
            }}>
              You have been securely logged out. Redirecting to login...
            </p>
            <div style={{
              width: '100%',
              height: '4px',
              background: colors.border,
              borderRadius: '2px',
              overflow: 'hidden'
            }}>
              <div style={{
                height: '100%',
                background: colors.gradient,
                borderRadius: '2px',
                animation: 'progressFill 2s ease-in-out',
                width: '100%'
              }}></div>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      {/* <div style={styles.profileHeader} className="fade-in">
        <h1 style={styles.profileTitle}>Admin Profile</h1>
        <p style={styles.profileSubtitle}>
          Manage your account settings and preferences
        </p>
      </div> */}

      {/* Membership Status */}
      <div style={{...styles.card, ...styles.membershipStatus}} className="card-hover fade-in">
        <div style={styles.statusPattern}></div>
        <div style={styles.profileImgPlaceholder}>
          <HiOutlineUserCircle size={isMobile ? 24 : 32} />
        </div>
        <div>
          <h3 style={styles.profileName}>
            {userData.full_name} 
            <span style={styles.adminBadge}>
              <HiOutlineShieldCheck size={isMobile ? 10 : 12} />
              {userData.role?.replace('-', ' ').toUpperCase() || 'ADMIN'}
            </span>
          </h3>
          <p style={{ 
            margin: 0, 
            opacity: 0.9, 
            display: 'flex', 
            alignItems: 'center', 
            gap: '8px',
            justifyContent: isMobile ? 'center' : 'flex-start',
            fontSize: isMobile ? '0.875rem' : '1rem'
          }}>
            <FiMail size={isMobile ? 12 : 14} />
            {userData.email}
          </p>
        </div>
        <div style={{ textAlign: isMobile ? 'center' : 'right' }}>
          <p style={{ 
            margin: '4px 0', 
            opacity: 0.9, 
            fontSize: isMobile ? '0.75rem' : '0.875rem', 
            display: 'flex', 
            alignItems: 'center', 
            gap: '6px', 
            justifyContent: isMobile ? 'center' : 'flex-end' 
          }}>
            <FiAward size={isMobile ? 12 : 14} />
            ID: {userData.membership_id}
          </p>
          <div style={{
            background: 'rgba(255, 255, 255, 0.2)',
            padding: '6px 12px',
            borderRadius: '16px',
            fontSize: isMobile ? '0.7rem' : '0.75rem',
            fontWeight: '600',
            backdropFilter: 'blur(10px)',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '4px'
          }}>
            <FiCheck size={isMobile ? 10 : 12} />
            ACTIVE
          </div>
        </div>
      </div>

      {/* Personal Details */}
      <div style={styles.card} className="card-hover fade-in">
        <div style={styles.sectionHeader}>
          <h4 style={styles.sectionTitle}>
            <FiUser style={styles.sectionIcon} />
            Personal Details
          </h4>
        </div>
        <div style={styles.formGrid}>
          <div style={styles.inputGroup}>
            <label style={styles.label}>First Name</label>
            <div style={{ position: 'relative' }}>
              <FiUser style={styles.inputIcon} />
              <input 
                type="text" 
                value={firstName} 
                disabled={!isEditing}
                style={styles.input}
              />
            </div>
          </div>
          <div style={styles.inputGroup}>
            <label style={styles.label}>Last Name</label>
            <div style={{ position: 'relative' }}>
              <FiUser style={styles.inputIcon} />
              <input 
                type="text" 
                value={lastName} 
                disabled={!isEditing}
                style={styles.input}
              />
            </div>
          </div>
          <div style={styles.inputGroup}>
            <label style={styles.label}>Email</label>
            <div style={{ position: 'relative' }}>
              <FiMail style={styles.inputIcon} />
              <input
                type="email"
                value={userData.email}
                disabled={!isEditing}
                style={styles.input}
              />
            </div>
          </div>
          <div style={styles.inputGroup}>
            <label style={styles.label}>Phone Number</label>
            <div style={{ position: 'relative' }}>
              <FiPhone style={styles.inputIcon} />
              <input
                type="tel"
                placeholder="+1 (555) 000-0000"
                disabled={!isEditing}
                style={styles.input}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Admin Information */}
      <div style={styles.card} className="card-hover fade-in">
        <div style={styles.sectionHeader}>
          <h4 style={styles.sectionTitle}>
            <HiOutlineCog style={styles.sectionIcon} />
            Admin Information
          </h4>
        </div>
        <div style={styles.adminInfoGrid}>
          <div>
            <label style={styles.label}>Admin Role</label>
            <div style={{ position: 'relative' }}>
              <HiOutlineShieldCheck style={styles.inputIcon} />
              <input 
                type="text" 
                value={userData.role?.replace('-', ' ').toUpperCase() || 'ADMINISTRATOR'} 
                disabled 
                style={styles.input}
              />
            </div>
          </div>
          <div>
            <label style={styles.label}>Member Since</label>
            <div style={{ position: 'relative' }}>
              <FiCalendar style={styles.inputIcon} />
              <input 
                type="text" 
                value={new Date().toLocaleDateString('en-US', { 
                  year: 'numeric', 
                  month: 'long', 
                  day: 'numeric' 
                })} 
                disabled 
                style={styles.input}
              />
            </div>
          </div>
        </div>

        <div style={{ marginTop: '20px' }}>
          <label style={styles.label}>Permissions</label>
          <div style={styles.permissionsList}>
            {userData.permissions && Array.isArray(userData.permissions) ? (
              userData.permissions.slice(0, 6).map((permission, index) => (
                <span key={index} style={styles.permissionTag}>
                  {getPermissionIcon(permission)}
                  {permission.replace(/-/g, ' ')}
                </span>
              ))
            ) : (
              <>
                <span style={styles.permissionTag}>
                  <FiUsers size={12} />
                  User Management
                </span>
                <span style={styles.permissionTag}>
                  <FiFileText size={12} />
                  Content Moderation
                </span>
                <span style={styles.permissionTag}>
                  <FiSettings size={12} />
                  System Config
                </span>
              </>
            )}
          </div>
        </div>
        
        <div style={styles.cardActions}>
          <button 
            style={styles.editBtn} 
            className="btn-hover" 
            onClick={handleEditToggle}
            disabled={saveLoading}
          >
            {saveLoading ? (
              <>
                <FiLoader size={16} style={{ animation: 'spin 1s linear infinite' }} />
                Saving...
              </>
            ) : isEditing ? (
              <>
                <FiSave size={16} />
                Save Changes
              </>
            ) : (
              <>
                <FiEdit2 size={16} />
                Edit Profile
              </>
            )}
          </button>
        </div>
      </div>

      {/* Membership Card */}
      <div style={styles.card} className="card-hover fade-in">
        <div style={styles.sectionHeader}>
          <h4 style={styles.sectionTitle}>
            <HiOutlineIdentification style={styles.sectionIcon} />
            Membership Card
          </h4>
        </div>
        <div style={styles.membershipCardContent}>
          <div>
            <div style={styles.membershipIdSection}>
              <FiAward size={isMobile ? 16 : 20} />
              <span style={{ 
                fontSize: isMobile ? '0.7rem' : '0.75rem', 
                opacity: 0.8, 
                fontWeight: '600', 
                letterSpacing: '1px' 
              }}>
                ADMIN ID CARD
              </span>
            </div>
            <h3 style={{ 
              margin: '0 0 8px 0', 
              fontSize: isMobile ? '1.25rem' : '1.5rem', 
              fontWeight: '700' 
            }}>
              {userData.full_name}
            </h3>
            <p style={{ 
              margin: '0 0 16px 0', 
              opacity: 0.9, 
              fontFamily: "'JetBrains Mono', monospace",
              fontSize: isMobile ? '0.875rem' : '1rem'
            }}>
              {userData.membership_id}
            </p>
            <div style={styles.membershipBadges}>
              <span style={{
                background: 'rgba(255, 255, 255, 0.2)',
                padding: '6px 12px',
                borderRadius: '12px',
                fontSize: isMobile ? '0.7rem' : '0.75rem',
                fontWeight: '600',
                backdropFilter: 'blur(10px)',
                display: 'flex',
                alignItems: 'center',
                gap: '4px'
              }}>
                <HiOutlineShieldCheck size={isMobile ? 10 : 12} />
                {userData.role?.replace('-', ' ').toUpperCase()}
              </span>
              <span style={{
                background: 'rgba(255, 255, 255, 0.2)',
                padding: '6px 12px',
                borderRadius: '12px',
                fontSize: isMobile ? '0.7rem' : '0.75rem',
                fontWeight: '600',
                backdropFilter: 'blur(10px)',
                display: 'flex',
                alignItems: 'center',
                gap: '4px'
              }}>
                <FiCheck size={isMobile ? 10 : 12} />
                VERIFIED
              </span>
            </div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{
              width: isMobile ? '60px' : '80px',
              height: isMobile ? '60px' : '80px',
              borderRadius: '50%',
              border: `3px solid rgba(255,255,255,0.3)`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'rgba(255,255,255,0.1)',
              backdropFilter: 'blur(10px)',
              margin: isMobile ? '0 auto 8px' : '0 auto 12px'
            }}>
              <HiOutlineUserCircle size={isMobile ? 24 : 32} />
            </div>
            <p style={{ 
              margin: '8px 0', 
              fontSize: isMobile ? '0.7rem' : '0.75rem', 
              opacity: 0.8 
            }}>
              Admin Since {new Date().getFullYear()}
            </p>
          </div>
        </div>
      </div>

      {/* Logout Button */}
      <div style={styles.logoutContainer} className="fade-in">
        <button 
          style={styles.logoutBtn} 
          className="logout-btn-hover" 
          onClick={handleLogout}
        >
          <FiLogOut size={16} />
          Logout
        </button>
      </div>
    </div>
  );
};

export default AdminProfile;